import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import dayjs from "dayjs";
import axiosInstance from "@API/axios";
import {
  GET_ACTIVE_EXTENSION_INFO,
  REQUEST_EXTENSION,
} from "@API/APIRoutes";
import usePostItem from "@Hooks/usePostItem";

const phaseLabel = {
  IA: "Impact Assessment submission",
  EXIT_PLAN: "Exit Plan submission",
};

const ExtensionRequestDialog = ({
  open,
  onClose,
  exitRequestId,
  categoryAssignmentId,
  onSubmitted,
}) => {
  const [activeInfo, setActiveInfo] = useState(null);
  const [requestedWeeks, setRequestedWeeks] = useState(1);
  const [reason, setReason] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const { loading: submitting, postItem } = usePostItem();

  useEffect(() => {
    if (!open || !categoryAssignmentId) return;

    setLoading(true);
    setError("");
    axiosInstance
      .get(`${GET_ACTIVE_EXTENSION_INFO}/${categoryAssignmentId}/active`)
      .then((res) => setActiveInfo(res.data))
      .catch((err) => {
        const message =
          err?.response?.data?.details ||
          err?.response?.data?.message ||
          err?.message ||
          "Failed to load extension details.";
        setError(message);
      })
      .finally(() => setLoading(false));
  }, [open, categoryAssignmentId]);

  const requestedDate = useMemo(() => {
    if (!activeInfo?.baseEscalationDate) return null;
    return dayjs(activeInfo.baseEscalationDate).add(requestedWeeks, "week");
  }, [activeInfo?.baseEscalationDate, requestedWeeks]);

  const handleClose = () => {
    setRequestedWeeks(1);
    setReason("");
    setError("");
    setActiveInfo(null);
    onClose();
  };

  const handleSubmit = async () => {
    setError("");
    const result = await postItem(
      REQUEST_EXTENSION,
      {
        exitRequestId,
        categoryAssignmentId,
        requestedWeeks,
        reason,
      },
      () => {},
      "Extension request submitted successfully!",
      "Failed to submit extension request.",
      true
    );
    if (!result) {
      setError("Failed to submit extension request.");
      return;
    }
    onSubmitted?.();
    handleClose();
  };

  const hasPendingRequest = Boolean(activeInfo?.pendingRequest);
  const canSubmit = activeInfo?.extensionAvailable && !hasPendingRequest;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Request Extension</DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Typography>Loading extension details...</Typography>
        ) : activeInfo ? (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            <Alert severity={canSubmit ? "info" : "warning"}>
              {activeInfo.extensionAvailable
                ? `You are requesting an ${phaseLabel[activeInfo.phase]} extension.`
                : activeInfo.unavailableReason}
            </Alert>

            {hasPendingRequest && (
              <Alert severity="warning">
                A pending {phaseLabel[activeInfo.phase]} extension request already exists.
              </Alert>
            )}

            <Box>
              <Typography variant="body2" color="text.secondary">
                Original escalation date
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {activeInfo.baseEscalationDate
                  ? dayjs(activeInfo.baseEscalationDate).format("MM/DD/YYYY")
                  : "-"}
              </Typography>
            </Box>

            <FormControl fullWidth size="small" disabled={!canSubmit}>
              <InputLabel>Extension Period</InputLabel>
              <Select
                label="Extension Period"
                value={requestedWeeks}
                onChange={(e) => setRequestedWeeks(Number(e.target.value))}
              >
                {Array.from({ length: 8 }, (_, index) => index + 1).map((week) => (
                  <MenuItem key={week} value={week}>
                    {week} {week === 1 ? "week" : "weeks"}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <Box>
              <Typography variant="body2" color="text.secondary">
                New requested escalation date
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {requestedDate ? requestedDate.format("MM/DD/YYYY") : "-"}
              </Typography>
            </Box>

            <TextField
              label="Reason"
              multiline
              minRows={3}
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              disabled={!canSubmit}
              fullWidth
            />
          </Box>
        ) : null}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={!canSubmit || submitting}
        >
          {submitting ? "Submitting..." : "Submit Request"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ExtensionRequestDialog;
